import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';
import { StepItemProps } from '@net0-calc/common/types';
import { Fragment } from 'react';
import styled from 'styled-components/macro';

interface SidebarStepNavProps<T, K> {
  steps: StepItemProps<T, K>[];
  currentStepIndex: number;
  onStepClick: (stepIndex: number) => void;
}

export function SidebarStepNav<T, K>({ steps, currentStepIndex, onStepClick }: SidebarStepNavProps<T, K>) {
  return (
    <Nav>
      {steps.map((step, index) => (
        <Fragment key={index}>
          {step.icon && (
            <StepItem
              role={index < currentStepIndex ? 'button' : undefined}
              tabIndex={index < currentStepIndex ? 0 : -1}
              title={index > currentStepIndex && step.description ? step.description : undefined}
              $isActive={index === currentStepIndex}
              $isCompleted={index < currentStepIndex}
              $isClickable={index < currentStepIndex}
              onClick={() => index < currentStepIndex && onStepClick(index)}
              onKeyDown={e => {
                if (index >= currentStepIndex) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onStepClick(index);
                }
              }}>
              <StepLabel>{step.title}</StepLabel>
            </StepItem>
          )}
        </Fragment>
      ))}
    </Nav>
  );
}

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StepItem = styled.div<{
  $isActive: boolean;
  $isCompleted: boolean;
  $isClickable: boolean;
}>`
  ${TYPOGRAPHY.bodyRegular16}
  color: ${({ $isActive, $isCompleted }) =>
    $isActive ? '#ffffff' : $isCompleted ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.65)'};
  font-weight: ${({ $isActive }) => ($isActive ? 600 : 400)};
  text-decoration: ${({ $isActive }) => ($isActive ? 'underline' : 'none')};
  text-underline-offset: 4px;
  padding: 12px 12px;
  margin: 0 -12px;
  border-radius: 6px;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
  transition: color 0.2s ease, background-color 0.2s ease;

  &:hover {
    ${({ $isClickable }) =>
      $isClickable
        ? `
    background-color: rgba(255,255,255,0.12);
    color: #ffffff;
  `
        : ''}
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.6);
    outline-offset: 2px;
  }

  @media ${DEVICE.laptop} {
    padding: 14px 12px;
  }
`;

const StepLabel = styled.span`
  white-space: pre-line;
`;
